import { useAction, useAtom } from '@reatom/npm-react'
import { motion, useDragControls } from 'motion/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, Typography, useHapticFeedback } from '@blade/ui-kit'
import { EyeClosed2Icon, EyeIcon, Refresh3Icon } from '@root/shared/ui-kit'

import {
  notificationCenterWidgetsVisibilityAtom,
  refreshNotificationCenterWidgetAction,
  toggleNotificationCenterWidgetAction,
} from '../../atoms'

import type { WidgetDragInfo } from './use-widget-rows-drag'
import type { NotificationCenterWidget } from '@root/widgets/notification-widgets/model'

export const WIDGET_SIZE = 48

type Props = {
  value: NotificationCenterWidget
  title: React.ReactNode
  icon?: React.ReactNode
  withRefresh?: boolean
  compact?: boolean
  /** When true, layout animation is off so only the drag transform stacks above siblings. */
  layout?: boolean
  onDragPrepare?: () => void
  onDragStart?: () => void
  onDragMove?: (info: WidgetDragInfo) => void
  onDragEnd?: () => void
}

export const NotificationCenterSettingsWidget: React.FC<Props> = ({
  value,
  title,
  icon,
  withRefresh = false,
  compact = false,
  layout = true,
  onDragPrepare,
  onDragStart,
  onDragMove,
  onDragEnd,
}) => {
  const { t } = useTranslation()
  const haptics = useHapticFeedback()
  const dragControls = useDragControls()
  const [dragging, setDragging] = React.useState(false)

  const [visible] = useAtom(
    (ctx) => ctx.spy(notificationCenterWidgetsVisibilityAtom)[value],
    [value],
  )
  const toggleVisibility = useAction(toggleNotificationCenterWidgetAction)
  const refresh = useAction(refreshNotificationCenterWidgetAction)

  const handleVisibilityClick = () => {
    haptics.impactOccurred('light')
    toggleVisibility(value)
  }
  const handleRefreshClick = () => {
    haptics.impactOccurred('light')
    refresh(value)
  }
  const handleDragHandlePointerDown = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    haptics.impactOccurred('light')
    // Measured before the widget can move, so the first move already has rows.
    onDragPrepare?.()
    dragControls.start(event)
  }

  const Icon = visible ? EyeIcon : EyeClosed2Icon

  return (
    <Root
      layout={layout && !dragging ? 'position' : false}
      drag
      dragSnapToOrigin
      dragMomentum={false}
      dragListener={false}
      dragControls={dragControls}
      animate={{ rotate: dragging ? -3 : 0 }}
      transition={{ rotate: { type: 'spring', stiffness: 400, damping: 30 } }}
      data-compact={compact || undefined}
      data-dragging={dragging || undefined}
      onDragStart={() => {
        setDragging(true)
        onDragStart?.()
      }}
      onDrag={(_, info) =>
        // Motion reports the pointer in page coordinates, the list measures in
        // viewport coordinates.
        onDragMove?.({
          y: info.point.y - window.scrollY,
          offsetX: info.offset.x,
        })
      }
      onDragEnd={() => {
        setDragging(false)
        onDragEnd?.()
      }}
    >
      <Handle
        type="button"
        aria-label={t('notification-center.reorder_widget')}
        onPointerDown={handleDragHandlePointerDown}
      >
        <Row />
        <Row />
        <Row />
      </Handle>
      <SeparatorVertical />
      <Body>
        <Heading>
          {icon}
          <HeadingTitle>{title}</HeadingTitle>
        </Heading>
        <Actions>
          {withRefresh ? (
            <RefreshButton
              onClick={handleRefreshClick}
              variant="ghost"
              size="xs"
              iconLeft={<Refresh3Icon size={16} />}
            />
          ) : undefined}
          <VisibilityButton
            onClick={handleVisibilityClick}
            aria-pressed={visible}
            data-hidden={!visible || undefined}
            variant="ghost"
            size="xs"
            iconLeft={<Icon size={18} />}
          />
        </Actions>
      </Body>
    </Root>
  )
}

const Root = styled(motion.div)`
  background: var(--backgroundA-2);
  border-radius: 8px;
  border: 1px solid var(--backgroundA-2);

  border-radius: 8px;
  box-sizing: border-box;

  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  min-width: 0;

  padding-inline-end: 8px;
  height: ${WIDGET_SIZE}px;
  width: 100%;

  transition: background 125ms ease-out;

  &[data-dragging] {
    cursor: grabbing;
    background: var(--backgroundA-3);
  }
`

const Handle = styled.button`
  display: grid;
  grid-template-rows: repeat(3, auto);

  gap: 3px;
  width: ${WIDGET_SIZE}px;
  height: 100%;
  justify-content: center;
  align-items: center;
  align-content: center;

  background: transparent;
  border: none;
  padding: 0;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`

const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 15px;
  height: ${WIDGET_SIZE}px;
  min-width: 0;
  padding-inline-start: 10px;

  ${Root}[data-compact] & {
    gap: 8px;
  }
`

const Row = styled.div`
  border-radius: 2px;
  background: var(--text-8);
  height: 2px;
  width: 20px;
`

const Heading = styled.span`
  color: var(--text-12);

  gap: 8px;
  display: grid;
  grid-auto-flow: column;
  align-content: start;
  align-items: center;
  justify-content: start;
  min-width: 0;
  padding-inline-start: 2px;

  ${Root}[data-compact] & {
    grid-auto-columns: minmax(0, auto);
    gap: 6px;
  }
`

const HeadingTitle = styled(Typography).attrs({
  variant: 'body2-semi-bold',
  component: 'span',
})`
  margin-top: 1px;
  min-width: 0;

  ${Root}[data-compact] & {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const RefreshButton = styled(Button)`
  color: var(--text-10);
`

const VisibilityButton = styled(Button)`
  color: var(--text-12);

  &[data-hidden] {
    color: var(--text-11);
  }
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 2px;
`

const SeparatorVertical = styled.div`
  height: 100%;
  width: 1px;
  background: var(--backgroundA-2);
`
